import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  Paper,
  useMediaQuery,
  useTheme as useMuiTheme
} from '@mui/material';
import AppIconButton from './ui/AppIconButton';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  TableRestaurant as TableIcon,
  ShoppingCart as CartIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  LocalOffer as OfferIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import api from '../api/api';
import SpiceLevelBadge from './SpiceLevelBadge';
import MenuItemImage from './MenuItemImage';
import { CURRENCY } from '../config/currency';

// Item styling for modern theme - soft/subtle colors
const itemKeywordStyles: { [key: string]: { emoji: string; color: string; gradient: string } } = {
  // Beverages - soft tones
  coffee: { emoji: '☕', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  cappuccino: { emoji: '☕', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  latte: { emoji: '☕', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  tea: { emoji: '🍵', color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF530 0%, #D1FAE520 100%)' },
  juice: { emoji: '🧃', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  smoothie: { emoji: '🥤', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
  // Main dishes - soft tones
  pizza: { emoji: '🍕', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
  burger: { emoji: '🍔', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  sandwich: { emoji: '🥪', color: '#7BA868', gradient: 'linear-gradient(135deg, #F7FEE730 0%, #ECFCCB20 100%)' },
  pasta: { emoji: '🍝', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
  noodles: { emoji: '🍜', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE830 0%, #FEF3C720 100%)' },
  biryani: { emoji: '🍛', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  curry: { emoji: '🍛', color: '#B07858', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  chicken: { emoji: '🍗', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  fish: { emoji: '🐟', color: '#6B9EB8', gradient: 'linear-gradient(135deg, #F0F9FF30 0%, #DBEAFE20 100%)' },
  soup: { emoji: '🍲', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  salad: { emoji: '🥗', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF430 0%, #DCFCE720 100%)' },
  fries: { emoji: '🍟', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE830 0%, #FEF3C720 100%)' },
  cake: { emoji: '🍰', color: '#A87898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
  icecream: { emoji: '🍨', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
  ice: { emoji: '🍨', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
  default: { emoji: '🍽️', color: '#6B7280', gradient: 'linear-gradient(135deg, #EEF2FF30 0%, #E0E7FF20 100%)' }
};

const getItemStyle = (itemName: string) => {
  const lowerName = itemName.toLowerCase();
  for (const [keyword, style] of Object.entries(itemKeywordStyles)) {
    if (keyword !== 'default' && lowerName.includes(keyword)) {
      return style;
    }
  }
  const firstLetter = lowerName.charAt(0);
  const colorMap: { [key: string]: { emoji: string; color: string; gradient: string } } = {
    a: { emoji: '🍎', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
    b: { emoji: '🥯', color: '#B89B68', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
    c: { emoji: '🥕', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
    f: { emoji: '🍟', color: '#7BA868', gradient: 'linear-gradient(135deg, #F7FEE730 0%, #ECFCCB20 100%)' },
    o: { emoji: '🍊', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
    s: { emoji: '🍓', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
  };
  return colorMap[firstLetter] || itemKeywordStyles.default;
};

interface OrderConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  qrCode: string;
  isLoading?: boolean;
  guestName?: string; // For anonymous table orders
  taxPreview?: {
    subtotal: number;
    cgstRate: number;
    cgstAmount: number;
    sgstRate: number;
    sgstAmount: number;
    igstRate: number;
    igstAmount: number;
    serviceChargeRate: number;
    serviceChargeAmount: number;
    discountAmount: number;
    taxType: string;
    total: number;
  } | null;
}

interface MenuItem {
  id: number;
  name: string;
  description?: string;
  price: number;
  imageUrl?: string;
  portions?: {
    id: number;
    portionName: string;
    effectivePrice: number;
  }[];
}

interface MenuCategory {
  id: number;
  name: string;
  items: MenuItem[];
}

const OrderConfirmationDialog: React.FC<OrderConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  qrCode,
  isLoading = false,
  guestName,
  taxPreview = null
}) => {
  const { user, guestSession } = useAuth();
  const { state, getCartTotal, getItemCount } = useCart();
  const { theme, currentThemeName } = useTheme();
  const isModernTheme = currentThemeName === 'modernDashboard';
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [menuCategories, setMenuCategories] = React.useState<MenuCategory[]>([]);
  
  const getCustomerInfo = () => {
    if (user) {
      return {
        name: user.username,
        type: 'Registered Customer',
        icon: <PersonIcon />,
        color: 'primary' as const
      };
    } else if (guestSession?.verified) {
      return {
        name: maskPhoneNumber(guestSession.phoneNumber),
        type: 'Guest Customer',
        icon: <PhoneIcon />,
        color: 'secondary' as const
      };
    } else if (guestName) {
      // Anonymous guest with name provided (for allowed guest table orders)
      return {
        name: guestName,
        type: 'Guest Customer',
        icon: <PersonIcon />,
        color: 'secondary' as const
      };
    }
    return {
      name: 'Guest',
      type: 'Table Order',
      icon: <PersonIcon />,
      color: 'secondary' as const
    };
  };
  
  const maskPhoneNumber = (phone: string) => {
    if (!phone || phone.length < 4) return phone;
    return `***-***-${phone.slice(-4)}`;
  };
  
  const customerInfo = getCustomerInfo();
  const cartTotal = getCartTotal();
  const itemCount = state.items.reduce((sum, item) => sum + item.quantity, 0); // Fix: calculate actual total quantity

  // Fetch menu data when dialog opens
  React.useEffect(() => {
    if (open && state.items.length > 0) {
      const fetchMenuData = async () => {
        try {
          let menuData: MenuCategory[] = [];
          
          if (state.vendorId) {
            const response = await api.get(`/vendors/${state.vendorId}/menu/categories`);
            menuData = response.data;
          } else if (state.qrCode) {
            const response = await api.get<{
              vendorId: number;
              vendorName: string;
              tableId?: number;
              tableName?: string;
              qrCode: string;
              categories: any[];
            }>(`/qr/${state.qrCode}/menu`);
            menuData = response.data.categories || [];
          }
          
          setMenuCategories(menuData);
        } catch (error) {
          console.error('Failed to fetch menu data:', error);
          // If we can't fetch menu data, continue without it
          setMenuCategories([]);
        }
      };
      
      fetchMenuData();
    }
  }, [open, state.vendorId, state.qrCode, state.items.length]);

  // Helper function to get menu item name
  // First checks the stored name in cart items, then falls back to fetched menu data
  const getMenuItemName = (itemId: number, cartItems: typeof state.items): string => {
    // First, try to get the name from the cart item itself (stored when item was added)
    const cartItem = cartItems.find(ci => ci.menuItem.id === itemId);
    if (cartItem?.menuItem?.name) {
      return cartItem.menuItem.name;
    }

    // Fallback to fetched menu data
    for (const category of menuCategories) {
      const item = category.items.find(item => item.id === itemId);
      if (item) {
        return item.name;
      }
    }
    return `Menu Item #${itemId}`;
  };

  // Helper function to get menu item imageUrl from fetched menu data
  const getMenuItemImageUrl = (itemId: number): string | undefined => {
    for (const category of menuCategories) {
      const item = category.items.find(item => item.id === itemId);
      if (item?.imageUrl) {
        return item.imageUrl;
      }
    }
    return undefined;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          boxShadow: isMobile ? 'none' : '0 10px 40px rgba(0, 0, 0, 0.1)',
          m: isMobile ? 0 : 2,
          // Add safe area ONLY when fullScreen on mobile
          ...(isMobile && {
            pt: 'max(12px, env(safe-area-inset-top, 0px))',
            pb: 'max(12px, env(safe-area-inset-bottom, 0px))',
            pl: 'max(0px, env(safe-area-inset-left, 0px))',
            pr: 'max(0px, env(safe-area-inset-right, 0px))'
          })
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 2,
        pt: isMobile ? 3 : 2.5,
        px: isMobile ? 2.5 : 3,
        borderBottom: '1px solid',
        borderColor: 'rgba(226, 232, 240, 0.8)'
      }}>
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          fontWeight="700"
          sx={{ 
            color: 'var(--text-primary)',
            letterSpacing: '-0.01em'
          }}
        >
          Confirm Your Order
        </Typography>
        <AppIconButton
          icon={<CloseIcon />}
          tooltip="Close"
          variant="danger"
          onClick={onClose}
          size="small"
          data-testid="order-confirmation-close"
        />
      </DialogTitle>
      
      <DialogContent sx={{ 
        px: isMobile ? 2.5 : 3, 
        py: isMobile ? 3 : 3,
        backgroundColor: '#fafbfc' /* basil-todo */
      }}>
        {/* Customer Identity Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: isMobile ? 2.5 : 3, 
            mb: 3, 
            bgcolor: 'rgba(219, 234, 254, 0.4)',
            border: '1px solid rgba(96, 165, 250, 0.25)',
            borderRadius: 2.5,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'var(--text-tertiary)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.7rem',
              mb: 1.5,
              display: 'block'
            }}
          >
            Ordering as:
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ 
              bgcolor: `${customerInfo.color}.main`, 
              width: isMobile ? 52 : 56, 
              height: isMobile ? 52 : 56,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
            }}>
              {customerInfo.icon}
            </Avatar>
            <Box>
              <Typography 
                variant={isMobile ? "body1" : "h6"} 
                fontWeight="700"
                sx={{ color: 'var(--text-primary)', mb: 0.5 }}
              >
                {customerInfo.name}
              </Typography>
              <Chip 
                label={customerInfo.type} 
                size="small" 
                color={customerInfo.color}
                variant="outlined"
                sx={{ 
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 24
                }}
              />
            </Box>
          </Box>
        </Paper>
        
        {/* Table/Location Info */}
        <Box 
          display="flex" 
          alignItems="center" 
          gap={1.5} 
          mb={3}
          sx={{
            p: 2,
            backgroundColor: 'var(--bg-surface)',
            borderRadius: 2,
            border: '1px solid rgba(226, 232, 240, 0.6)',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
          }}
        >
          <TableIcon sx={{ color: 'var(--text-tertiary)', fontSize: 22 }} />
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
            Table/QR Code: <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{qrCode}</span>
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3, borderColor: 'rgba(226, 232, 240, 0.6)' }} />
        
        {/* Order Summary */}
        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
          <CartIcon sx={{ color: 'var(--text-link)', fontSize: 24 }} /> 
          <Typography 
            variant={isMobile ? "h6" : "h6"} 
            fontWeight="700"
            sx={{ color: 'var(--text-primary)', letterSpacing: '-0.01em' }}
          >
            Order Summary
          </Typography>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          {state.items && state.items.length > 0 ? (
            (() => {
              // Group cart items by menu item ID (same logic as checkout)
              const groupedItems = state.items.reduce((groups, ci) => {
                const key = ci.menuItem.id;
                if (!groups[key]) {
                  groups[key] = [];
                }
                groups[key].push(ci);
                return groups;
              }, {} as { [key: number]: typeof state.items });

              return Object.entries(groupedItems).map(([menuItemId, cartItems]) => {
                const totalQuantity = cartItems.reduce((sum, ci) => sum + ci.quantity, 0);
                const totalPrice = cartItems.reduce((sum, ci) => sum + (ci.price * ci.quantity), 0);
                const hasMultiplePortions = cartItems.length > 1;
                const itemName = getMenuItemName(Number(menuItemId), cartItems);
                const itemStyle = isModernTheme ? getItemStyle(itemName) : null;
                const itemImageUrl = getMenuItemImageUrl(Number(menuItemId));

                return (
                  <Box 
                    key={menuItemId} 
                    sx={{
                      backgroundColor: 'var(--bg-surface)',
                      borderRadius: 2.5,
                      p: isMobile ? 2.5 : 3,
                      mb: 2.5,
                      border: isModernTheme && itemStyle ? `1px solid ${itemStyle.color}25` : '1px solid rgba(226, 232, 240, 0.8)',
                      boxShadow: isModernTheme && itemStyle ? `0 2px 8px ${itemStyle.color}10` : '0 1px 3px rgba(0, 0, 0, 0.04)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: isModernTheme && itemStyle ? `0 4px 12px ${itemStyle.color}20` : '0 2px 8px rgba(0, 0, 0, 0.08)',
                        borderColor: isModernTheme && itemStyle ? `${itemStyle.color}40` : 'rgba(96, 165, 250, 0.3)'
                      }
                    }}
                  >
                    {/* Item Header */}
                    <Box sx={{ mb: hasMultiplePortions ? 2.5 : 0 }}>
                      <Box display="flex" alignItems="start" justifyContent="space-between">
                        {/* Item Image or Emoji */}
                        {itemImageUrl ? (
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              overflow: 'hidden',
                              mr: 2,
                              flexShrink: 0,
                            }}
                          >
                            <MenuItemImage imageUrl={itemImageUrl} itemName={itemName} size="thumbnail" />
                          </Box>
                        ) : isModernTheme && itemStyle ? (
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              background: itemStyle.gradient,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                              flexShrink: 0,
                              border: `1px solid ${itemStyle.color}20`
                            }}
                          >
                            <span style={{ fontSize: '1.75rem' }}>{itemStyle.emoji}</span>
                          </Box>
                        ) : null}
                        <Box flex={1}>
                          <Typography 
                            variant="body1" 
                            fontWeight="700" 
                            sx={{ 
                              color: 'var(--text-primary)',
                              mb: hasMultiplePortions ? 0 : 1.5,
                              fontSize: isMobile ? '1rem' : '1.05rem',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            {itemName}
                          </Typography>
                          {!hasMultiplePortions && (
                            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5} mt={1}>
                              {cartItems[0].portionName && (
                                <Chip
                                  label={cartItems[0].portionName}
                                  size="small"
                                  sx={isModernTheme && itemStyle ? {
                                    height: 28,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    backgroundColor: `${itemStyle.color}15`,
                                    color: itemStyle.color,
                                    border: `1px solid ${itemStyle.color}40`,
                                    borderRadius: '8px'
                                  } : {
                                    height: 28,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    backgroundColor: 'rgba(241, 245, 249, 1)',
                                    color: 'var(--text-secondary)',
                                    border: '1px solid rgba(203, 213, 225, 0.6)',
                                    borderRadius: '8px'
                                  }}
                                />
                              )}
                              {cartItems[0].requestedSpiceLevel && (
                                <SpiceLevelBadge spiceLevel={cartItems[0].requestedSpiceLevel} size="small" />
                              )}
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: isModernTheme && itemStyle ? itemStyle.color : 'var(--text-link)',
                                  fontWeight: 600,
                                  fontSize: '0.875rem'
                                }}
                              >
                                {CURRENCY.symbol}{cartItems[0].price.toFixed(2)} each
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>
                                Qty: {cartItems[0].quantity}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        {!hasMultiplePortions && (
                          <Typography 
                            variant="h6" 
                            fontWeight="700" 
                            sx={{ 
                              color: isModernTheme && itemStyle ? itemStyle.color : 'var(--text-primary)',
                              ml: 2,
                              fontSize: isMobile ? '1.1rem' : '1.25rem'
                            }}
                          >
                            {CURRENCY.symbol}{(cartItems[0].price * cartItems[0].quantity).toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Portion Details for Multiple Portions */}
                    {hasMultiplePortions && (
                      <Box sx={{ mb: 2.5 }}>
                        {/* Mobile Card Layout */}
                        {isMobile ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {cartItems.map((ci, index) => (
                              <Box 
                                key={index} 
                                sx={{
                                  backgroundColor: 'rgba(248, 250, 252, 1)',
                                  borderRadius: 2,
                                  p: 2.5,
                                  border: '1px solid rgba(226, 232, 240, 0.8)'
                                }}
                              >
                                <Box display="flex" alignItems="start" justifyContent="space-between" mb={1.5}>
                                  <Box flex={1}>
                                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                                      <Chip
                                        label={ci.portionName || 'Standard'}
                                        size="small"
                                        sx={isModernTheme && itemStyle ? {
                                          height: 28,
                                          fontSize: '0.8rem',
                                          fontWeight: 600,
                                          backgroundColor: `${itemStyle.color}15`,
                                          color: itemStyle.color,
                                          border: `1px solid ${itemStyle.color}40`,
                                          borderRadius: '8px'
                                        } : {
                                          height: 28,
                                          fontSize: '0.8rem',
                                          fontWeight: 600,
                                          backgroundColor: 'var(--bg-surface)',
                                          color: 'var(--text-secondary)',
                                          border: '1px solid rgba(203, 213, 225, 0.6)',
                                          borderRadius: '8px'
                                        }}
                                      />
                                      {ci.requestedSpiceLevel && (
                                        <SpiceLevelBadge spiceLevel={ci.requestedSpiceLevel} size="small" />
                                      )}
                                    </Box>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: isModernTheme && itemStyle ? itemStyle.color : 'var(--text-link)',
                                        fontWeight: 600,
                                        fontSize: '0.875rem'
                                      }}
                                    >
                                      {CURRENCY.symbol}{ci.price.toFixed(2)} each
                                    </Typography>
                                  </Box>
                                  <Typography 
                                    variant="h6" 
                                    fontWeight="700" 
                                    sx={{ 
                                      color: isModernTheme && itemStyle ? itemStyle.color : 'var(--text-primary)',
                                      ml: 2,
                                      fontSize: '1.1rem'
                                    }}
                                  >
                                    {CURRENCY.symbol}{(ci.price * ci.quantity).toFixed(2)}
                                  </Typography>
                                </Box>
                                <Box 
                                  display="flex" 
                                  alignItems="center" 
                                  justifyContent="center"
                                  sx={{
                                    pt: 1.5,
                                    borderTop: '1px solid rgba(226, 232, 240, 0.6)'
                                  }}
                                >
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: 'var(--text-tertiary)',
                                      fontWeight: 600
                                    }}
                                  >
                                    Quantity: {ci.quantity}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          /* Desktop Table Layout */
                          <Box>
                            <Box 
                              display="grid" 
                              gridTemplateColumns="5fr 3fr 2fr 2fr" 
                              gap={2} 
                              alignItems="center" 
                              sx={{
                                py: 1.5,
                                px: 2,
                                backgroundColor: 'rgba(241, 245, 249, 1)',
                                borderRadius: '10px 10px 0 0',
                                borderBottom: '1px solid rgba(226, 232, 240, 0.8)'
                              }}
                            >
                              <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                                Portion
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem', textAlign: 'center' }}>
                                Price
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem', textAlign: 'center' }}>
                                Qty
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem', textAlign: 'right' }}>
                                Subtotal
                              </Typography>
                            </Box>
                            {cartItems.map((ci, index) => (
                              <Box 
                                key={index} 
                                display="grid" 
                                gridTemplateColumns="5fr 3fr 2fr 2fr" 
                                gap={2} 
                                alignItems="center" 
                                sx={{
                                  py: 2,
                                  px: 2,
                                  backgroundColor: 'var(--bg-surface)',
                                  borderBottom: index < cartItems.length - 1 ? '1px solid rgba(226, 232, 240, 0.5)' : 'none',
                                  borderRadius: index === cartItems.length - 1 ? '0 0 10px 10px' : 0
                                }}
                              >
                                <Box display="flex" alignItems="center" gap={1.5}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                    {ci.portionName || 'Standard'}
                                  </Typography>
                                  {ci.requestedSpiceLevel && (
                                    <SpiceLevelBadge spiceLevel={ci.requestedSpiceLevel} size="small" />
                                  )}
                                </Box>
                                <Typography variant="body2" sx={{ color: 'var(--text-link)', fontWeight: 600, textAlign: 'center' }}>
                                  {CURRENCY.symbol}{ci.price.toFixed(2)}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center' }}>
                                  {ci.quantity}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--text-primary)', textAlign: 'right' }}>
                                  {CURRENCY.symbol}{(ci.price * ci.quantity).toFixed(2)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Toppings */}
                    {cartItems.some(ci => ci.toppings && ci.toppings.length > 0) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'var(--text-tertiary)',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontSize: '0.7rem',
                            mb: 1,
                            display: 'block'
                          }}
                        >
                          Add-ons:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {cartItems.flatMap(ci => ci.toppings || []).map((toppingId, idx) => (
                            <Chip
                              key={idx}
                              label={`Add-on #${toppingId}`}
                              size="small"
                              icon={<OfferIcon />}
                              sx={{
                                height: 28,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                backgroundColor: 'rgba(253, 230, 138, 0.3)',
                                color: 'var(--status-warn-text)',
                                border: '1px solid rgba(245, 158, 11, 0.3)',
                                borderRadius: '8px',
                                '& .MuiChip-icon': {
                                  color: 'var(--status-warn-text)',
                                  fontSize: '1rem'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Comments */}
                    {cartItems.some(ci => ci.comment) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'var(--text-tertiary)',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontSize: '0.7rem',
                            mb: 1,
                            display: 'block'
                          }}
                        >
                          Special Instructions:
                        </Typography>
                        {cartItems.filter(ci => ci.comment).map((ci, idx) => (
                          <Typography 
                            key={idx} 
                            variant="body2" 
                            sx={{ 
                              color: 'var(--text-secondary)',
                              fontWeight: 500,
                              lineHeight: 1.6,
                              pl: 1.5,
                              borderLeft: '3px solid rgba(96, 165, 250, 0.4)',
                              py: 0.5
                            }}
                          >
                            {ci.comment}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {/* Item Total for Multiple Portions */}
                    {hasMultiplePortions && (
                      <Box 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="space-between" 
                        sx={{
                          pt: 2.5,
                          borderTop: '2px solid rgba(226, 232, 240, 0.8)'
                        }}
                      >
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 700,
                            color: 'var(--text-primary)'
                          }}
                        >
                          Total ({totalQuantity} {totalQuantity === 1 ? 'item' : 'items'})
                        </Typography>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700,
                            color: 'var(--text-primary)',
                            fontSize: isMobile ? '1.2rem' : '1.35rem'
                          }}
                        >
                          {CURRENCY.symbol}{totalPrice.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              });
            })()
          ) : (
            <Box 
              sx={{ 
                p: 4,
                textAlign: 'center',
                backgroundColor: 'var(--bg-surface)',
                borderRadius: 2.5,
                border: '1px solid rgba(226, 232, 240, 0.8)'
              }}
            >
              <Typography variant="body2" sx={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>
                No items in cart
              </Typography>
            </Box>
          )}
        </Box>
        
        <Divider sx={{ my: 3, borderColor: 'rgba(226, 232, 240, 0.6)' }} />

        {/* Total Section with Tax Breakdown */}
        <Box
          sx={{
            p: isMobile ? 2.5 : 3,
            backgroundColor: 'rgba(219, 234, 254, 0.3)',
            borderRadius: 2.5,
            border: '1px solid rgba(96, 165, 250, 0.25)',
            mb: 3,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
          }}
        >
          {/* Item Count Header */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'var(--text-tertiary)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.7rem',
                display: 'block',
                mb: 0.5
              }}
            >
              Total Items
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'var(--text-primary)',
                fontSize: isMobile ? '1.1rem' : '1.25rem'
              }}
            >
              {itemCount} {itemCount === 1 ? 'item' : 'items'}
            </Typography>
          </Box>

          <Divider sx={{ my: 2, borderColor: 'rgba(96, 165, 250, 0.2)' }} />

          {/* Price Breakdown */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'var(--text-tertiary)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.7rem',
                display: 'block',
                mb: 1.5
              }}
            >
              Price Breakdown
            </Typography>

            {/* Subtotal */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                Subtotal
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                {CURRENCY.symbol}{(taxPreview?.subtotal || cartTotal).toFixed(2)}
              </Typography>
            </Box>

            {/* Tax Breakdown - Show only if we have tax data */}
            {taxPreview && (
              <>
                {/* Discount — before taxes/service charge: they are computed on
                    the discounted base, so display order mirrors calculation order */}
                {taxPreview.discountAmount > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                      Discount
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--status-ok)', fontWeight: 600 }}>
                      -{CURRENCY.symbol}{taxPreview.discountAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {/* CGST */}
                {taxPreview.cgstAmount > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                      CGST ({taxPreview.cgstRate}%)
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                      {CURRENCY.symbol}{taxPreview.cgstAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {/* SGST */}
                {taxPreview.sgstAmount > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                      SGST ({taxPreview.sgstRate}%)
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                      {CURRENCY.symbol}{taxPreview.sgstAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {/* IGST */}
                {taxPreview.igstAmount > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                      IGST ({taxPreview.igstRate}%)
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                      {CURRENCY.symbol}{taxPreview.igstAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {/* Service Charge */}
                {taxPreview.serviceChargeAmount > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                      Service Charge ({taxPreview.serviceChargeRate}%)
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                      {CURRENCY.symbol}{taxPreview.serviceChargeAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}

              </>
            )}
          </Box>

          <Divider sx={{ my: 2, borderColor: 'rgba(96, 165, 250, 0.3)' }} />

          {/* Total Amount */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant="body1"
              sx={{
                color: 'var(--text-tertiary)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.875rem'
              }}
            >
              Total Amount
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: 'var(--text-link)',
                fontSize: isMobile ? '1.5rem' : '1.75rem',
                letterSpacing: '-0.02em'
              }}
            >
              {CURRENCY.symbol}{(taxPreview?.total || cartTotal).toFixed(2)}
            </Typography>
          </Box>
        </Box>
        
        {/* Important Notice */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: isMobile ? 2 : 2.5, 
            bgcolor: 'rgba(254, 243, 199, 0.4)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: 2,
            mb: 2,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'var(--status-warn-text)',
              fontWeight: 500,
              lineHeight: 1.6,
              fontSize: isMobile ? '0.875rem' : '0.9rem'
            }}
          >
            <strong style={{ fontWeight: 700 }}>Please note:</strong> Once confirmed, this order will be sent to the kitchen 
            and cannot be cancelled. Please review your order carefully before confirming.
          </Typography>
        </Paper>
        
        {/* Additional confirmation for modifications */}
        {guestSession && (
          <Box mt={2}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'var(--text-tertiary)',
                fontWeight: 500,
                textAlign: 'center',
                display: 'block',
                fontSize: '0.8rem'
              }}
            >
              By confirming, you agree that the order will be placed under {maskPhoneNumber(guestSession.phoneNumber)}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions 
        sx={{ 
          p: isMobile ? 2.5 : 3,
          pt: 2,
          gap: isMobile ? 2 : 2.5,
          borderTop: '1px solid rgba(226, 232, 240, 0.6)',
          backgroundColor: '#fafbfc', /* basil-todo */
          flexDirection: isMobile ? 'column' : 'row'
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth={isMobile}
          sx={{
            borderRadius: '10px !important',
            textTransform: 'none !important',
            fontWeight: '600 !important',
            fontSize: '1rem !important',
            backgroundColor: 'var(--bg-surface) !important',
            borderColor: 'var(--border-subtle) !important',
            borderWidth: '2px !important',
            color: 'var(--text-secondary) !important',
            boxShadow: 'none !important',
            px: isMobile ? 3 : 4,
            py: 1.75,
            minWidth: isMobile ? 'auto' : 140,
            height: 48,
            '&:hover': {
              backgroundColor: 'var(--bg-sunken) !important',
              borderColor: 'var(--border-strong) !important',
              borderWidth: '2px !important',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05) !important',
            },
          }}
        >
          Go Back
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          variant="contained"
          fullWidth={isMobile}
          startIcon={<CheckIcon />}
          sx={isModernTheme ? {
            borderRadius: '12px !important',
            textTransform: 'none !important',
            fontWeight: '700 !important',
            fontSize: '1rem !important',
            background: 'linear-gradient(135deg, #059669 0%, #10B981 100%) !important',
            border: 'none !important',
            color: 'var(--text-inverse) !important',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3) !important',
            px: isMobile ? 3 : 4,
            py: 1.75,
            minWidth: isMobile ? 'auto' : 140,
            height: 48,
            '&:hover': {
              background: 'linear-gradient(135deg, #047857 0%, #059669 100%) !important',
              boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4) !important',
            },
            '&:disabled': {
              background: 'var(--bg-sunken) !important',
              color: 'var(--text-tertiary) !important',
              boxShadow: 'none !important',
            },
          } : {
            borderRadius: '10px !important',
            textTransform: 'none !important',
            fontWeight: '700 !important',
            fontSize: '1rem !important',
            backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
            borderColor: 'rgba(96, 165, 250, 0.31) !important',
            border: '2px solid !important',
            color: 'var(--text-link) !important',
            boxShadow: '0 1px 3px rgba(37, 99, 235, 0.15) !important',
            px: isMobile ? 3 : 4,
            py: 1.75,
            minWidth: isMobile ? 'auto' : 140,
            height: 48,
            '&:hover': {
              backgroundColor: 'rgba(96, 165, 250, 0.3) !important',
              borderColor: 'rgba(96, 165, 250, 0.4) !important',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.2) !important',
            },
            '&:disabled': {
              backgroundColor: 'var(--bg-sunken) !important',
              borderColor: 'var(--border-default) !important',
              color: 'var(--text-tertiary) !important',
              boxShadow: 'none !important',
            },
          }}
        >
          {isLoading ? 'Placing Order...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderConfirmationDialog;